/**
 * Dice on the game board.
 *
 * The same body as the initiative tray (`dice3d/dieAssets.js`: chamfered
 * polyhedron, card-stock atlas, bump map) for every side count, spawned into
 * the table scene. Three things a die on a table has to get right that a die
 * on a tray never meets:
 *
 * 1. **It rests on what is under it.** A raycast straight down finds the top
 *    surface at the die's spot: the table, a card, a stack, a pile. The die's
 *    center sits exactly its own rest height above that. Nothing clips.
 * 2. **A die dropped on a card is part of that card.** The drop stores where
 *    the die lies in the card's own frame (`diceInstance.on`), and from then
 *    on the die is drawn from the card every frame: drag the card and it comes
 *    along, tap it and the die swings with it, lift a stack and it lifts. That
 *    is the shared state, so both seats and a reload see the same die on the
 *    same card, and a card carrying six dice moves them as one body. A flip
 *    leaves the die where it is. When the card leaves the table the die stays
 *    put and is reported back to the table (`onOrphaned`).
 * 3. **It rolls in place.** The throw is the tray's billiard (`dice3d/animate.js`)
 *    with a small stage centred on the die's own spot, so it tumbles around
 *    where it lies and comes home to the exact same point. A die rolled on a
 *    card lands back on that card.
 *
 * Everything per die lives in `mesh.userData.die`; the board keeps the meshes
 * in its own map and ticks them once per frame through `tickDice`.
 */

import * as THREE from 'three';

import { beginRoll, makeDie, restHeight, stepDie } from './dice3d/animate';
import { bank, preloadDiceAudio, rattle, rollEnd, rollStart } from './dice3d/diceAudio';
import { cloneDieMaterial, DICE_PALETTES, dieAssets, getDicePalette, loadDiceArtwork, skinDieAssets } from './dice3d/dieAssets';
import {
  dieGeometry, faceIndexForValue, faceQuaternion, qAxisAngle, qMul, qNormalize,
} from './dice3d/geometry';

export const DICE_CONFIGS = {
  d4: { faces: 4, label: 'd4' },
  d6: { faces: 6, label: 'd6' },
  d8: { faces: 8, label: 'd8' },
  d10: { faces: 10, label: 'd10' },
  d12: { faces: 12, label: 'd12' },
  d20: { faces: 20, label: 'd20' },
};

/**
 * Circumradius in world units per body. A card is 11.1 wide; a real d20 is
 * about a third of a card across, the others a touch smaller, so the set reads
 * as one set rather than six mismatched props.
 */
export const DIE_RADIUS = { d4: 2.0, d6: 2.05, d8: 2.0, d10: 2.0, d12: 1.95, d20: 1.95 };

/** The table top. Cards float a hair above it; a die on bare table rests here. */
const TABLE_Y = 0;
/** How high a picked-up die hangs over its rest height. */
const LIFT = 2.6;
/** Seconds a die takes to drop onto its new surface. */
const DROP_SECONDS = 0.18;
/**
 * A die set by hand tumbles like a throw, only on the spot and short: the
 * stage is barely wider than the die, the flick lower, fewer turns, and it
 * is over in well under a second. Same sound, sized to the shorter throw.
 */
const SET_FACE = { stageRadii: 0.9, duration: 0.62, turnA: 9, turnB: 6, launch: 2.1 };
/**
 * Half-extent of the billiard stage around the die's spot, in radii. Small:
 * the die should tumble on the spot, not wander across the table.
 */
const STAGE_RADII = 2.2;
/** Frames between height probes of a die on the open table. Staggered per die. */
const PROBE_EVERY = 12;
/** Seconds a die's card may be missing from the table before the die is reported loose. */
const ORPHAN_S = 1;
/** A resting die keeps its numeral within this much of "toward the viewer". */
const YAW_JITTER = 0.22;
/** The probe starts this high, above any stack the board can build. */
const PROBE_FROM = 80;

/** Motion blur, as on the tray (see `DiceGL`): spacing in angle, not time. */
const GHOSTS = 6;
const SMEAR_ARC = 1.15;
const BLUR_FROM = 9;
const SHADOW_GHOSTS = 2;

const _down = new THREE.Vector3(0, -1, 0);
const _origin = new THREE.Vector3();
const _axis = new THREE.Vector3();
const _ghostAxis = new THREE.Vector3();
const _ghostTurn = new THREE.Quaternion();
const raycaster = new THREE.Raycaster();

function sidesOf(dieType) {
  return DICE_CONFIGS[dieType]?.faces ?? 6;
}

/** Rotate a plain vector by a plain quaternion. */
function rotate(q, v) {
  const ix = q.w * v[0] + q.y * v[2] - q.z * v[1];
  const iy = q.w * v[1] + q.z * v[0] - q.x * v[2];
  const iz = q.w * v[2] + q.x * v[1] - q.y * v[0];
  const iw = -q.x * v[0] - q.y * v[1] - q.z * v[2];
  return [
    ix * q.w + iw * -q.x + iy * -q.z - iz * -q.y,
    iy * q.w + iw * -q.y + iz * -q.x - ix * -q.z,
    iz * q.w + iw * -q.z + ix * -q.y - iy * -q.x,
  ];
}

/** How far above the surface the center sits when the body lies in pose `q`. */
function restOffset(geometry, q, radius) {
  let lowest = Infinity;
  for (const v of geometry.vertices) lowest = Math.min(lowest, rotate(q, v)[1]);
  return -lowest * radius;
}

/**
 * The pose that shows `value`, turned so the numeral reads upright from where
 * the camera stands. Each client turns its own dice toward its own seat: the
 * rolled number is what is shared, not the yaw. A little jitter so a row of
 * dice does not look stamped.
 */
function facingPose(die, value, camera, mesh) {
  const face = faceIndexForValue(die.geometry, value);
  const flat = faceQuaternion(die.geometry, Math.max(0, face));
  let yaw = 0;
  if (camera) {
    const ax = mesh.position.x - camera.position.x;
    const az = mesh.position.z - camera.position.z;
    if (Math.hypot(ax, az) > 1e-6) yaw = Math.atan2(-ax, -az);
  }
  yaw += (Math.random() * 2 - 1) * YAW_JITTER;
  return qNormalize(qMul(qAxisAngle([0, 1, 0], yaw), flat));
}

function applyPose(mesh, q) {
  mesh.quaternion.set(q.x, q.y, q.z, q.w);
}

// ─── Mesh lifecycle ───

/**
 * A die wears the palette it was dropped in (`diceInstance.palette`, stamped
 * by the server from its owner's choice), so the two seats' dice can differ
 * on one table and a change of palette never recolours what already lies.
 * A palette this build does not know (the other seat wears a skin this
 * client has not shipped) falls back to the player's own pick.
 */
function dieBody(sides, palette) {
  const recipe = palette ? DICE_PALETTES[palette] : null;
  return recipe ? skinDieAssets(sides, 1, recipe) : dieAssets(sides);
}

/** The palette a die this seat drops right now will wear. */
export const ownDicePalette = () => getDicePalette().id;

/** Put a different palette on a die already on the table (the server's stamp beat the drop). */
export function reskinDiceMesh(mesh, palette) {
  const inst = mesh.userData?.diceInstance;
  const die = mesh.userData?.die;
  if (!inst || !die) return;
  inst.palette = palette ?? null;
  const { material } = dieBody(die.sides, inst.palette);
  mesh.material.dispose();
  mesh.material = cloneDieMaterial(material);
  for (const ghostMat of die.ghostMats) ghostMat.dispose();
  die.ghostMats = die.ghosts.map((ghost) => {
    const m = cloneDieMaterial(material);
    m.transparent = true;
    m.opacity = 0;
    m.depthWrite = false;
    ghost.material = m;
    return m;
  });
}

export function createDiceMesh(diceInstance) {
  const sides = sidesOf(diceInstance.dieType);
  const geometry = dieGeometry(sides);
  const { geometry: body, material } = dieBody(sides, diceInstance.palette);
  const radius = DIE_RADIUS[diceInstance.dieType] ?? 2;

  const mesh = new THREE.Mesh(body, cloneDieMaterial(material));
  mesh.scale.setScalar(radius);
  mesh.castShadow = true;
  mesh.receiveShadow = true;

  const die = {
    sides,
    radius,
    geometry,
    /** The surface point under the die's center, in world space. */
    anchor: { x: diceInstance.x, y: TABLE_Y, z: diceInstance.z },
    /** Seconds the card the die is attached to has been off the table. */
    gone: 0,
    orphaned: false,
    /** The tray's simulation while the die is in the air. */
    anim: null,
    target: null,
    ghosts: [],
    ghostMats: [],
    dragging: false,
    /** The drop onto a new surface: from this height, this far along. */
    dropFrom: null,
    dropT: 1,
    probeIn: Math.floor(Math.random() * PROBE_EVERY),
    /** A fresh die has not met the camera yet; the first settle turns it. */
    unfaced: true,
    restY: 0,
  };
  mesh.userData = { type: 'dice', diceInstance, die };

  const pose = faceQuaternion(geometry, Math.max(0, faceIndexForValue(geometry, diceInstance.value)));
  die.restY = restOffset(geometry, pose, radius);
  applyPose(mesh, pose);
  mesh.position.set(diceInstance.x, TABLE_Y + die.restY, diceInstance.z);

  loadDiceArtwork();
  preloadDiceAudio();
  return mesh;
}

/** Take the die off the table and free what only it owned. */
export function disposeDiceMesh(mesh) {
  const die = mesh.userData?.die;
  mesh.parent?.remove(mesh);
  mesh.material?.dispose?.();
  if (!die) return;
  for (const ghost of die.ghosts) ghost.parent?.remove(ghost);
  for (const mat of die.ghostMats) mat.dispose();
  die.ghosts = [];
  die.ghostMats = [];
  if (die.anim) rollEnd();
  die.anim = null;
}

// ─── Placement ───

/**
 * What lies under (x, z): the highest visible surface and its top. The table
 * when nothing does.
 */
function probeSurface(x, z, surfaces) {
  _origin.set(x, PROBE_FROM, z);
  raycaster.set(_origin, _down);
  raycaster.far = PROBE_FROM * 2;
  let top = TABLE_Y;
  let carrier = null;
  for (const hit of raycaster.intersectObjects(surfaces, false)) {
    if (!hit.object.visible || hit.point.y <= top) continue;
    top = hit.point.y;
    carrier = hit.object;
  }
  return { top, carrier };
}

/**
 * The card's heading on the table. Read off its intent orientation, with the
 * flip taken back out: a flip turns the card over about the table's depth
 * axis, which mirrors its frame, and a die is not glued to the underside. So
 * the die's frame is the card's face-up heading, and a flipping card leaves
 * the die where it lies.
 */
function cardYaw(card) {
  const e = card.userData?.intentEuler;
  if (e) return e.z - (e.x + Math.PI / 2);
  _axis.set(1, 0, 0).applyQuaternion(card.quaternion);
  return Math.hypot(_axis.x, _axis.z) < 1e-6 ? 0 : -Math.atan2(_axis.z, _axis.x);
}

/** A table point in the card's frame. */
function toCardFrame(card, x, z) {
  const yaw = cardYaw(card);
  const dx = x - card.position.x;
  const dz = z - card.position.z;
  return { u: dx * Math.cos(yaw) - dz * Math.sin(yaw), w: dx * Math.sin(yaw) + dz * Math.cos(yaw) };
}

/** Where a point in the card's frame lies on the table, into `out`. */
function fromCardFrame(card, u, w, out) {
  const yaw = cardYaw(card);
  out.x = card.position.x + u * Math.cos(yaw) + w * Math.sin(yaw);
  out.z = card.position.z - u * Math.sin(yaw) + w * Math.cos(yaw);
}

/** Half the card's thickness, in world units: its top over its center. */
function cardLift(card) {
  const g = card.geometry;
  if (!g.boundingBox) g.computeBoundingBox();
  return ((g.boundingBox.max.z - g.boundingBox.min.z) / 2) * card.scale.z;
}

function onTable(card) {
  return !!card && !!card.parent && card.visible;
}

/** The card mesh a die's instance is attached to, if it is on the table. */
function attachedCard(inst, ctx) {
  const card = inst.on && ctx.cardMesh ? ctx.cardMesh(inst.on.cardId) : null;
  return onTable(card) ? card : null;
}

function anchorOnCard(die, inst, card) {
  fromCardFrame(card, inst.on.u, inst.on.w, die.anchor);
  die.anchor.y = card.position.y + cardLift(card);
  inst.x = die.anchor.x;
  inst.z = die.anchor.z;
}

/** Where the instance says the die lies, in world space. */
export function dieSpot(mesh, ctx = {}) {
  const inst = mesh.userData.diceInstance;
  const card = attachedCard(inst, ctx);
  if (!card) return { x: inst.x, z: inst.z };
  const out = {};
  fromCardFrame(card, inst.on.u, inst.on.w, out);
  return out;
}

function beginDrop(mesh, die) {
  die.dropFrom = mesh.position.y;
  die.dropT = 0;
}

/**
 * Pick the die up. It hangs in the air under the pointer until it is put
 * down again. A die grabbed mid-roll lands on its number at once.
 */
export function liftDie(mesh) {
  const die = mesh.userData.die;
  die.dragging = true;
  if (die.anim) finishRoll(mesh, die);
  mesh.position.y = die.anchor.y + die.restY + LIFT;
}

/**
 * Lay the die where its instance says: on its card, in the card's frame, or
 * at its table spot on whatever is there. The drop animates from wherever the
 * mesh is now.
 *
 * @param ctx.cardMesh  function from card id to its mesh on the table
 * @param ctx.surfaces  function returning the meshes a loose die can rest on
 * @param ctx.camera    the viewer, for turning a fresh die's numeral upright
 */
export function placeDie(mesh, ctx = {}) {
  const die = mesh.userData.die;
  const inst = mesh.userData.diceInstance;
  die.dragging = false;
  die.gone = 0;
  die.orphaned = false;
  const card = attachedCard(inst, ctx);
  if (card) {
    anchorOnCard(die, inst, card);
  } else {
    die.anchor.x = inst.x;
    die.anchor.z = inst.z;
    die.anchor.y = probeSurface(inst.x, inst.z, ctx.surfaces ? ctx.surfaces() : []).top;
  }
  if (die.unfaced) {
    die.unfaced = false;
    setDieFaceUp(mesh, inst.value, ctx);
  }
  beginDrop(mesh, die);
}

/**
 * Put the die down where it is. A card underneath takes it: the instance
 * records the die in that card's frame. Anything else leaves it loose on the
 * table at this spot.
 */
export function settleDie(mesh, ctx = {}) {
  const inst = mesh.userData.diceInstance;
  const { x, z } = mesh.position;
  const { carrier } = probeSurface(x, z, ctx.surfaces ? ctx.surfaces() : []);
  const cardId = carrier?.userData?.type === 'card' ? carrier.userData.cardInstance?.id : null;
  inst.x = x;
  inst.z = z;
  inst.on = cardId ? { cardId, ...toCardFrame(carrier, x, z) } : null;
  placeDie(mesh, ctx);
}

/**
 * Keep the anchor where the die belongs this frame. An attached die follows
 * its card; one whose card has left the table waits ORPHAN_S, then is
 * reported loose once. A loose die re-reads its height now and then, so a
 * pile growing or a card sliding under it is sat on, not clipped through.
 */
function follow(mesh, die, dt, ctx) {
  const inst = mesh.userData.diceInstance;
  if (inst.on) {
    const card = attachedCard(inst, ctx);
    if (card) {
      anchorOnCard(die, inst, card);
      die.gone = 0;
      die.orphaned = false;
    } else if (!die.orphaned && (die.gone += dt) >= ORPHAN_S) {
      die.orphaned = true;
      ctx.onOrphaned?.(mesh);
    }
    return;
  }
  if (ctx.surfaces && --die.probeIn <= 0) {
    die.probeIn = PROBE_EVERY;
    const { top } = probeSurface(die.anchor.x, die.anchor.z, ctx.surfaces());
    if (Math.abs(top - die.anchor.y) > 1e-4) {
      die.anchor.y = top;
      beginDrop(mesh, die);
    }
  }
}

// ─── Showing a number ───

/** Turn the die onto `value` at once, numeral toward the viewer. */
export function setDieFaceUp(mesh, value, ctx = {}) {
  const die = mesh.userData.die;
  if (faceIndexForValue(die.geometry, value) < 0) return;
  if (die.anim) finishRoll(mesh, die);
  const pose = facingPose(die, value, ctx.camera, mesh);
  die.restY = restOffset(die.geometry, pose, die.radius);
  applyPose(mesh, pose);
  mesh.userData.diceInstance.value = value;
  if (!die.dragging) mesh.position.y = die.anchor.y + die.restY;
}

/**
 * Turn the die onto `value` by hand: the throw's own tumble, cut short and
 * kept on the spot, so the die visibly rolls over onto the face the player
 * asked for. A die in the air is set at once instead.
 */
export function turnDieTo(mesh, value, ctx = {}) {
  const die = mesh.userData.die;
  if (faceIndexForValue(die.geometry, value) < 0) return;
  if (die.dragging) {
    setDieFaceUp(mesh, value, ctx);
    return;
  }
  const sign = () => (Math.random() < 0.5 ? 1 : -1);
  startRoll(mesh, die, value, ctx, SET_FACE.stageRadii, {
    duration: SET_FACE.duration, turnA: sign() * SET_FACE.turnA, turnB: sign() * SET_FACE.turnB,
  }, SET_FACE.launch);
}

/**
 * Tumble onto `value`. The path is the tray's billiard on a small stage around
 * the die's own spot, so it comes home to exactly where it lies (on its card,
 * if it lies on one).
 */
export function animateDiceRoll(mesh, value, ctx = {}) {
  const die = mesh.userData.die;
  if (faceIndexForValue(die.geometry, value) < 0) return;
  if (die.dragging) {
    setDieFaceUp(mesh, value, ctx);
    return;
  }
  startRoll(mesh, die, value, ctx, STAGE_RADII);
}

function startRoll(mesh, die, value, ctx, stageRadii, tour = null, launch = null) {
  if (die.anim) finishRoll(mesh, die);
  die.dropT = 1;
  die.target = facingPose(die, value, ctx.camera, mesh);
  die.restY = restOffset(die.geometry, die.target, die.radius);
  mesh.userData.diceInstance.value = value;

  const stage = die.radius * stageRadii;
  const anim = makeDie(Math.random, [0, 0], die.radius, [stage, stage]);
  beginRoll(anim, die.target, 0, Math.random, tour);
  if (launch !== null) anim.v[1] = launch;
  die.anim = anim;
  if (mesh.parent) ensureGhosts(mesh, die);
  rollStart(anim.tour.duration);
}

function finishRoll(mesh, die) {
  die.anim = null;
  for (const ghost of die.ghosts) ghost.visible = false;
  applyPose(mesh, die.target);
  mesh.position.set(die.anchor.x, die.anchor.y + die.restY, die.anchor.z);
  rollEnd();
}

// ─── Motion blur ───

function ensureGhosts(mesh, die) {
  if (die.ghosts.length > 0) return;
  for (let g = 0; g < GHOSTS; g++) {
    const mat = cloneDieMaterial(mesh.material);
    mat.transparent = true;
    mat.opacity = 0;
    mat.depthWrite = false;
    const ghost = new THREE.Mesh(mesh.geometry, mat);
    ghost.visible = false;
    ghost.castShadow = g % Math.ceil(GHOSTS / SHADOW_GHOSTS) === 0;
    // A smear is not a thing you can click.
    ghost.raycast = () => {};
    mesh.parent.add(ghost);
    die.ghosts.push(ghost);
    die.ghostMats.push(mat);
  }
}

function smear(mesh, die, anim) {
  const w = anim.w;
  const speed = Math.hypot(w[0], w[1], w[2]);
  const chain = die.ghosts.length;
  const trail = speed > BLUR_FROM ? Math.min(chain, Math.round(speed / BLUR_FROM) + 2) : 0;
  const step = trail > 0 ? SMEAR_ARC / (trail * speed) : 0;
  for (let g = 0; g < chain; g++) {
    const ghost = die.ghosts[g];
    if (g >= trail) {
      ghost.visible = false;
      continue;
    }
    const back = (g + 1) * step;
    ghost.visible = true;
    _ghostAxis.set(w[0] / speed, w[1] / speed, w[2] / speed);
    _ghostTurn.setFromAxisAngle(_ghostAxis, -speed * back);
    ghost.quaternion.copy(_ghostTurn).multiply(mesh.quaternion);
    ghost.position.set(
      mesh.position.x - anim.v[0] * back,
      mesh.position.y - anim.v[1] * back,
      mesh.position.z - anim.v[2] * back,
    );
    ghost.scale.setScalar(die.radius * (1 + (g + 1) * 0.012));
    die.ghostMats[g].opacity = 0.62 * (1 - (g + 1) / (trail + 1)) ** 1.35;
  }
}

// ─── The frame ───

function stepRoll(mesh, die, dt) {
  const anim = die.anim;
  stepDie(anim, dt, Math.random);

  const hit = anim.impact;
  if (hit !== null) {
    // The invisible cushion bangs like the tray's. The table bounce stays
    // mute under the wheel; the arrival rattles.
    if (hit.kind === 'wall') bank(hit.strength * 0.8);
    else if (hit.kind === 'settle') rattle();
  }

  if (anim.phase === 'rest') {
    finishRoll(mesh, die);
    return;
  }

  // A tumbling body's center rides higher than a resting one: the faster it
  // spins the nearer its lowest point is to a corner. Lift it so nothing
  // ploughs through the card it is rolling on.
  const spin = Math.hypot(anim.w[0], anim.w[1], anim.w[2]);
  const lift = (die.radius - die.restY) * Math.min(1, spin / 12) * 0.9;
  mesh.position.set(
    die.anchor.x + anim.p[0],
    die.anchor.y + die.restY + lift + (anim.p[1] - restHeight(anim.radius)),
    die.anchor.z + anim.p[2],
  );
  applyPose(mesh, anim.q);
  smear(mesh, die, anim);
}

/**
 * Once per frame for every die on the board. Rolling dice advance their
 * throw, resting dice follow their card and drop onto their surface. A
 * dragged die is the pointer's; it is left alone.
 */
export function tickDice(meshes, dt, ctx = {}) {
  for (const mesh of meshes) {
    const die = mesh.userData?.die;
    if (!die || die.dragging) continue;

    follow(mesh, die, dt, ctx);

    if (die.anim) {
      stepRoll(mesh, die, dt);
      continue;
    }

    mesh.position.x = die.anchor.x;
    mesh.position.z = die.anchor.z;
    const restAt = die.anchor.y + die.restY;
    if (die.dropT < 1) {
      die.dropT = Math.min(1, die.dropT + dt / DROP_SECONDS);
      const eased = 1 - (1 - die.dropT) ** 3;
      mesh.position.y = die.dropFrom + (restAt - die.dropFrom) * eased;
    } else {
      mesh.position.y = restAt;
    }
  }
}
